import React, {useCallback, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Button, Empty, List, Popover, Spin, Tag} from "antd";
import {ClipboardList} from "lucide-react";
import {APIv1} from "../../../api";
import './AuditLogsButton.css';

const AUDIT_ACTION_COLOR_MAP = {
    create: "green",
    update: "blue",
    delete: "red",
};

const AUDIT_LOGS_PREVIEW_LIMIT = 10;
const AUDIT_CHANGED_FIELDS_PREVIEW_LIMIT = 3;

const getAuditActionType = (action) => String(action || "").toLowerCase();

const getChangedFields = (changes) => {
    if (!changes) {
        return [];
    }

    if (typeof changes === "object") {
        return Object.keys(changes);
    }

    try {
        const parsedChanges = JSON.parse(changes);
        return parsedChanges && typeof parsedChanges === "object" ? Object.keys(parsedChanges) : [];
    } catch (err) {
        return [];
    }
};

const AuditLogsButton = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [isAuditPopoverOpen, setIsAuditPopoverOpen] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLogsLoading, setAuditLogsLoading] = useState(false);
    const userData = JSON.parse(localStorage.getItem("user"));
    const isStaff = Boolean(userData?.data?.is_admin);

    const fetchLatestAuditLogs = useCallback(async () => {
        if (!userData?.token || !isStaff) {
            return;
        }

        setAuditLogsLoading(true);

        try {
            const response = await APIv1.get("/audit-logs/list/", {
                params: {
                    page: 1,
                    page_size: AUDIT_LOGS_PREVIEW_LIMIT,
                },
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            });
            const results = Array.isArray(response.data?.results)
                ? response.data.results
                : Array.isArray(response.data)
                    ? response.data
                    : [];

            setAuditLogs(results.slice(0, AUDIT_LOGS_PREVIEW_LIMIT));
        } catch (err) {
            console.error("Something went wrong:", err);
            setAuditLogs([]);
        } finally {
            setAuditLogsLoading(false);
        }
    }, [isStaff, userData?.token]);

    const handleAuditPopoverChange = (open) => {
        setIsAuditPopoverOpen(open);

        if (open) {
            fetchLatestAuditLogs();
        }
    };

    const handleGoToAuditLogs = () => {
        setIsAuditPopoverOpen(false);
        navigate("/audit-logs");
    };

    if (!isStaff) {
        return null;
    }

    const auditLogsPopoverContent = (
        <div className="analysis__audit-popover">
            <Spin spinning={auditLogsLoading}>
                {auditLogs.length ? (
                    <List
                        className="analysis__audit-list"
                        dataSource={auditLogs}
                        renderItem={(log) => {
                            const actionType = getAuditActionType(log.action);
                            const changedFields = getChangedFields(log.changes);
                            const visibleChangedFields = changedFields.slice(0, AUDIT_CHANGED_FIELDS_PREVIEW_LIMIT);

                            return (
                                <List.Item className="analysis__audit-item">
                                    <div className="analysis__audit-row">
                                        <Tag color={AUDIT_ACTION_COLOR_MAP[actionType] || "default"}>
                                            {t(`pages.auditLogs.actions.${actionType}`, actionType || "-")}
                                        </Tag>
                                        <div className="analysis__audit-details">
                                            <span className="analysis__audit-object">{log.object_repr || "-"}</span>
                                            <span className="analysis__audit-actor">{log.actor || "-"}</span>
                                        </div>
                                        <div className="analysis__audit-changed-fields">
                                            {changedFields.length > 0 ? (
                                                <>
                                                    {visibleChangedFields.map((field) => (
                                                        <Tag key={field} className="analysis__audit-field-tag">
                                                            {field}
                                                        </Tag>
                                                    ))}
                                                    {changedFields.length > AUDIT_CHANGED_FIELDS_PREVIEW_LIMIT && (
                                                        <Tag className="analysis__audit-field-tag">
                                                            +{changedFields.length - AUDIT_CHANGED_FIELDS_PREVIEW_LIMIT}
                                                        </Tag>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="analysis__audit-empty-fields">-</span>
                                            )}
                                        </div>
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                ) : (
                    <Empty description={t("analysis.auditLogs.empty")}/>
                )}
            </Spin>

            <div className="analysis__audit-popover-footer">
                <Button type="primary" block onClick={handleGoToAuditLogs}>
                    {t("analysis.auditLogs.detailsButton")}
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            title={t("analysis.auditLogs.title")}
            content={auditLogsPopoverContent}
            trigger="click"
            placement="bottomRight"
            open={isAuditPopoverOpen}
            onOpenChange={handleAuditPopoverChange}
            overlayClassName="analysis__audit-popover-overlay"
        >
            <Button
                type="primary"
                icon={<ClipboardList size={16}/>}
                className="analysis__audit-button"
            >
                {t("analysis.auditLogs.button")}
            </Button>
        </Popover>
    );
};

export default AuditLogsButton;
